import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import {
  buildAbsoluteUrl,
  buildOrganizationSchema,
  buildWebSiteSchema,
  SITE_NAME,
  SITE_NAME_AR,
  DEFAULT_OG_IMAGE,
} from '../Utils/seoConfig';

const PageSEO = ({
  title,
  description,
  keywords,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  locale = 'en',
  type = 'website',
  noindex = false,
  jsonLd = [],
  includeSiteSchemas = false,
}) => {
  const canonical = buildAbsoluteUrl(path);
  const ogLocale = locale === 'ar' ? 'ar_SA' : 'en_US';
  const altLocale = locale === 'ar' ? 'en_US' : 'ar_SA';
  const siteName = locale === 'ar' ? SITE_NAME_AR : SITE_NAME;

  const schemas = useMemo(() => {
    const base = Array.isArray(jsonLd) ? [...jsonLd] : jsonLd ? [jsonLd] : [];
    if (includeSiteSchemas) {
      base.unshift(buildWebSiteSchema(locale));
      base.unshift(buildOrganizationSchema(locale));
    }
    return base.filter(Boolean);
  }, [jsonLd, includeSiteSchemas, locale]);

  return (
    <Helmet htmlAttributes={{ lang: locale, dir: locale === 'ar' ? 'rtl' : 'ltr' }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta httpEquiv="content-language" content={locale === 'ar' ? 'ar-SA' : 'en-SA'} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="ar" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:alt" content={siteName} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={altLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={siteName} />

      <meta name="geo.region" content="SA-04" />
      <meta name="geo.placename" content="Al Khobar, Saudi Arabia" />
      <meta name="geo.position" content="26.1901017;50.1876654" />
      <meta name="ICBM" content="26.1901017, 50.1876654" />
      <meta name="author" content={siteName} />
      <meta name="publisher" content={siteName} />

      {schemas.map((schema, index) => (
        <script
          key={`${schema['@type']}-${schema['@id'] || schema.headline || schema.name || index}`}
          type="application/ld+json"
        >
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default PageSEO;
